import { get } from 'lodash';
import i18next from 'i18next';

import { CATEGORY_TYPES_OBJECT, getPropertyNoneValue } from '@commons/constants/categoryTypes';

import { ingredient as ingredientService } from '@services/ingredient';
import clientService from '@services/client';
import storageAreaService from '@services/storageArea';

export const computeSupplierProduct = (supplierProduct) => {
  const formattedName = supplierProduct.name.trim();

  const formattedSKU = supplierProduct.sku ? supplierProduct.sku.trim() : '-';

  const status = i18next.t(supplierProduct.active ? 'GENERAL.ACTIVE' : 'GENERAL.INACTIVE');

  const formattedSupplierName = get(supplierProduct, 'supplier.name', '-').trim();

  const formattedEntityName = get(supplierProduct, 'entity.name', '-').trim();

  const formattedCategoryName = supplierProduct.category
    ? supplierProduct.category.trim()
    : i18next.t('GENERAL.SELECT_NONE_FEMININE');

  const formattedSubCategoryName = supplierProduct.subCategory
    ? supplierProduct.subCategory.trim()
    : i18next.t('GENERAL.SELECT_NONE_FEMININE');

  return {
    ...supplierProduct,
    status,
    sku: formattedSKU,
    name: formattedName,
    entityName: formattedEntityName,
    category: formattedCategoryName,
    supplierName: formattedSupplierName,
    subCategory: formattedSubCategoryName,
  };
};

export const computeAdvancedFiltersList = async ({ clientId, columns }) => {
  const { categories, subCategories } = await clientService.getCategoriesAndSubCategories(
    CATEGORY_TYPES_OBJECT.SUPPLIER_PRODUCT,
  );

  let ingredientOfClients, inventoryListTemplatesOfClient, storageAreasOfClient;

  try {
    ingredientOfClients = await ingredientService.getIngredients(false);

    inventoryListTemplatesOfClient = await clientService.getInventoryListTemplates({
      withMappings: false,
      filterByUserCatalog: true,
    });

    storageAreasOfClient = await storageAreaService.getStorageAreasByClientId();
  } catch {
    // silent catch, no need to warn user
  }

  const result = columns.reduce((result, column) => {
    const { propertyKey, filterType } = column;

    if (filterType === 'numeric') {
      result.push(column);
    }

    if (propertyKey === 'category' && categories) {
      const choices = categories.map(({ id, name }) => {
        const formattedName = name || getPropertyNoneValue();

        return {
          name: formattedName,
          id,
          category: formattedName,
        };
      });

      result.push({
        ...column,
        list: choices,
      });
    }

    if (propertyKey === 'subCategory' && subCategories) {
      const choices = subCategories.map(({ id, name }) => {
        const formattedName = name || getPropertyNoneValue();

        return {
          name: formattedName,
          id,
          subCategory: formattedName,
        };
      });

      result.push({
        ...column,
        list: choices,
      });
    }

    if (propertyKey === 'entityName' && ingredientOfClients) {
      const choices = ingredientOfClients.map(({ id, name }, index) => ({
        name,
        entityName: name,
        id: index,
        entityId: id,
      }));

      choices.push({
        id: -1,
        entityId: null,
        entityName: null,
        name: i18next.t('GENERAL.SELECT_NONE_MASCULINE'),
      });

      result.push({
        ...column,
        list: choices,
      });
    }

    if (propertyKey === 'priceEditableAtReception') {
      result.push({
        ...column,
        list: [
          {
            id: 0,
            itemValue: true,
            name: i18next.t('GENERAL.YES'),
            value: i18next.t('GENERAL.YES'),
            ['priceEditableAtReception']: i18next.t('GENERAL.YES'),
          },
          {
            id: 1,
            itemValue: false,
            name: i18next.t('GENERAL.NO'),
            value: i18next.t('GENERAL.NO'),
            ['priceEditableAtReception']: i18next.t('GENERAL.NO'),
          },
        ],
      });
    }

    if (propertyKey === 'inventoryListTemplate' && inventoryListTemplatesOfClient) {
      const choices = inventoryListTemplatesOfClient.map(({ id, name }, index) => ({
        id: index,
        name,
        inventoryListTemplateId: id,
        inventoryListTemplateName: name,
      }));

      choices.push({
        id: -1,
        name: i18next.t('GENERAL.SELECT_NONE_MASCULINE'),
        inventoryListTemplateId: null,
        inventoryListTemplateName: null,
      });

      result.push({
        ...column,
        list: choices,
      });
    }

    if (propertyKey === 'storageArea' && storageAreasOfClient) {
      const choices = storageAreasOfClient.map(({ id, name }, index) => ({
        id: index,
        name,
        storageAreaId: id,
        storageAreaName: name,
      }));

      choices.push({
        id: -1,
        name: i18next.t('GENERAL.SELECT_NONE_MASCULINE'),
        storageAreaId: null,
        storageAreaName: null,
      });

      result.push({
        ...column,
        list: choices,
      });
    }

    return result;
  }, []);

  return result;
};
